/**
 * Phase 125 — Standalone Venue/Turf Registration at /arenax/host/venue.
 *
 * Supply-side marketplace listing for ANY entity owning a sports facility:
 * private turfs, sports bars, rooftop courts, pickleball centers,
 * community grounds, indoor arenas, badminton/tennis courts.
 *
 * Critically: never gated on `academy_owner` role.  Pure entity ownership.
 * On success, grants the `publish_venue` capability and shows a city-
 * native celebration overlay.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useArenaxCapabilities } from '@/lib/arenaxCapabilities';
import ArenaxLayout from './ArenaxLayout';
import { ArenaxWordmark } from './ArenaxWordmark';
import ArenaxLeafletPicker from './ArenaxLeafletPicker';
import { ArenaxImageUploader, ArenaxGalleryUploader } from './ArenaxImageUploader';
import ArenaxActivationCelebration from './ArenaxActivationCelebration';
import {
  MapPin, ArrowLeft, ArrowRight, CheckCircle2, Loader2, Sun, Lightbulb,
  Coffee, Building, Trees, Tent, Home, Trophy,
} from 'lucide-react';

const SPORTS = [
  'Football', 'Cricket', 'Hockey', 'Badminton', 'Basketball',
  'Tennis', 'Volleyball', 'Pickleball', 'Table Tennis',
];

const ENTITY_TYPES = [
  { id: 'private_turf', label: 'Private Turf', icon: Trees, hint: 'Football, cricket, multi-sport' },
  { id: 'pickleball_center', label: 'Pickleball Center', icon: Trophy, hint: 'Dedicated pickleball courts' },
  { id: 'indoor_arena', label: 'Indoor Arena', icon: Building, hint: 'Climate-controlled multi-sport' },
  { id: 'badminton_court', label: 'Badminton Court', icon: Trophy, hint: 'Wooden / synthetic courts' },
  { id: 'tennis_court', label: 'Tennis Court', icon: Trophy, hint: 'Clay or hard court' },
  { id: 'rooftop_court', label: 'Rooftop Court', icon: Home, hint: 'High-rise sports space' },
  { id: 'sports_bar', label: 'Sports Bar', icon: Coffee, hint: 'Watch + play hybrid' },
  { id: 'community_ground', label: 'Community Ground', icon: Tent, hint: 'Public-friendly venue' },
];

const SURFACES = [
  { id: 'turf', label: 'Artificial Turf' },
  { id: 'grass', label: 'Natural Grass' },
  { id: 'clay', label: 'Clay' },
  { id: 'wooden', label: 'Wooden' },
  { id: 'synthetic', label: 'Synthetic' },
  { id: 'concrete', label: 'Concrete' },
];

const LIGHTING = [
  { id: 'floodlit', label: 'Floodlit · Night-ready', icon: Lightbulb },
  { id: 'natural', label: 'Natural light only', icon: Sun },
  { id: 'indoor', label: 'Indoor · Climate-controlled', icon: Lightbulb },
];

const AMENITIES = [
  'Parking', 'Changing Rooms', 'Showers', 'Drinking Water',
  'Café', 'Equipment Rental', 'CCTV', 'First Aid', 'Wi-Fi', 'Seating',
];

export default function ArenaxHostVenue() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { refresh: refreshCapabilities } = useArenaxCapabilities();
  const [submitting, setSubmitting] = useState(false);
  const [celebrating, setCelebrating] = useState(null); // {city}
  const [form, setForm] = useState({
    name: '',
    entity_type: 'private_turf',
    sports: ['Football'],
    city: '',
    area: '',
    address: '',
    landmark: '',
    geo: { lat: null, lng: null },
    surface_type: 'turf',
    lighting: 'floodlit',
    amenities: [],
    infrastructure: {
      turf_count: 1, ground_size_sqft: null, indoor: false, outdoor: true,
      floodlights: true, washrooms: true, parking: true, seating_capacity: null,
    },
    booking: {
      open_slots: true, base_price_per_hour: null,
      peak_price_per_hour: null, subscription_available: false,
    },
    phone: '',
    tagline: '',
    cover_image: null,
    logo_image: null,
    gallery_images: [],
    publish_immediately: true,
  });

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const updateNested = (parent, k, v) => setForm(prev => ({
    ...prev,
    [parent]: { ...prev[parent], [k]: v },
  }));
  const toggleArr = (key, v) => setForm(prev => ({
    ...prev,
    [key]: prev[key].includes(v) ? prev[key].filter(x => x !== v) : [...prev[key], v],
  }));

  const canSubmit = (form.name || '').trim().length > 1
    && form.sports.length > 0
    && (form.city || '').trim().length > 0;

  const submit = async () => {
    if (!user) { navigate('/arenax/auth/login'); return; }
    if (!canSubmit) { toast.error('Name, sports and city are required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        cover_image: form.cover_image?.path || form.cover_image?.url || null,
        logo_image: form.logo_image?.path || form.logo_image?.url || null,
        gallery_images: form.gallery_images.map((g, i) => ({
          url: g.url || g.path,
          display_order: g.display_order ?? i,
          caption: g.caption || null,
        })),
      };
      await api.post('/arenax/host/venue', payload);
      // Phase 178.5 — refresh capability ledger so `publish_venue` is
      // hydrated before the user lands on /arenax/host/venue/manage.
      try { await refreshCapabilities(); } catch (_) { /* non-fatal */ }
      setCelebrating({ city: form.city });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not list venue');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <ArenaxLayout>
        <section className="max-w-md mx-auto px-5 py-20 text-center arenax-themed">
          <MapPin className="h-10 w-10 mx-auto mb-3" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
          <h1 className="text-2xl font-bold mb-2"
              style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, serif' }}>
            Sign in to list your venue
          </h1>
          <p className="text-sm mb-6" style={{ color: 'hsl(var(--ax-text-muted))' }}>
            Drop your ground into ArenaX's city map.
          </p>
          <button
            data-testid="host-venue-signin-cta"
            onClick={() => navigate('/arenax/auth/login')}
            className="px-5 py-2.5 rounded-xl text-sm font-bold"
            style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
          >
            Sign in to ArenaX
          </button>
        </section>
      </ArenaxLayout>
    );
  }

  return (
    <ArenaxLayout>
      {celebrating && (
        <ArenaxActivationCelebration
          kind="venue"
          city={celebrating.city}
          continueLabel="See it in the city pulse"
          onContinue={() => navigate('/arenax/host?activated=venue')}
        />
      )}

      <section className="max-w-3xl mx-auto px-5 py-10 arenax-themed" data-testid="arenax-host-venue-page">
        <div className="text-center mb-8 space-y-2">
          <div className="inline-flex items-center justify-center mb-1">
            <ArenaxWordmark size="md" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight"
              style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, "Times New Roman", serif' }}>
            Drop your ground into the city map.
          </h1>
          <p className="text-sm max-w-md mx-auto" style={{ color: 'hsl(var(--ax-text-muted))' }}>
            Publish slots. Monetise underused time. Show up in the city pulse tonight.
          </p>
        </div>

        <div className="arenax-card arenax-operational-form p-6 sm:p-8 space-y-8" style={{ borderRadius: 16 }}>

          {/* ---- Entity Type ---- */}
          <Section title="What kind of venue are you?" subtitle="This shapes how your venue appears in city discovery.">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="host-venue-entity-types">
              {ENTITY_TYPES.map(et => {
                const Icon = et.icon;
                const active = form.entity_type === et.id;
                return (
                  <button
                    key={et.id}
                    type="button"
                    onClick={() => update('entity_type', et.id)}
                    data-testid={`host-venue-entity-${et.id}`}
                    className="text-left px-3 py-3 rounded-xl border transition-all hover:-translate-y-0.5"
                    style={{
                      background: active ? 'var(--ax-primary-gradient)' : 'var(--ax-card-bg-soft)',
                      color: active ? 'hsl(var(--ax-button-text))' : 'hsl(var(--ax-text))',
                      borderColor: active ? 'transparent' : 'var(--ax-border-quiet)',
                    }}
                  >
                    <Icon className="h-4 w-4 mb-1.5"
                          style={{ color: active ? 'hsl(var(--ax-button-text))' : 'hsl(var(--ax-accent-cyan))' }} />
                    <div className="text-xs font-bold leading-tight">{et.label}</div>
                    <div className="text-[10px] mt-0.5 opacity-80 leading-tight">{et.hint}</div>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* ---- Identity ---- */}
          <Section title="Identity">
            <Field label="Venue name">
              <input
                data-testid="host-venue-name"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                placeholder="e.g. HSR Sports Arena"
                className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
              />
            </Field>
            <Field label="Sports supported">
              <div className="flex flex-wrap gap-2" data-testid="host-venue-sports">
                {SPORTS.map(s => {
                  const active = form.sports.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      data-testid={`host-venue-sport-${s.toLowerCase().replace(/\s/g, '-')}`}
                      onClick={() => toggleArr('sports', s)}
                      className={`arenax-chip ${active ? 'is-active' : ''} text-xs px-3 py-1.5 rounded-full transition-all`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Tagline" helper="A short, memorable line.">
              <input
                data-testid="host-venue-tagline"
                value={form.tagline}
                onChange={e => update('tagline', e.target.value)}
                placeholder="Where the city plays past midnight."
                className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
              />
            </Field>
          </Section>

          {/* ---- Location ---- */}
          <Section title="Location" subtitle="Drop your ground into the city map — players find you by neighborhood.">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="space-y-4">
                <Field label="City">
                  <input
                    data-testid="host-venue-city"
                    value={form.city}
                    onChange={e => update('city', e.target.value)}
                    placeholder="Bengaluru"
                    className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                  />
                </Field>
                <Field label="Area / Neighborhood">
                  <input
                    data-testid="host-venue-area"
                    value={form.area}
                    onChange={e => update('area', e.target.value)}
                    placeholder="HSR Layout"
                    className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                  />
                </Field>
                <Field label="Full address">
                  <input
                    data-testid="host-venue-address"
                    value={form.address}
                    onChange={e => update('address', e.target.value)}
                    placeholder="14th Cross, Sector 7"
                    className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                  />
                </Field>
                <Field label="Landmark" helper="The detail that helps players actually find you.">
                  <input
                    data-testid="host-venue-landmark"
                    value={form.landmark}
                    onChange={e => update('landmark', e.target.value)}
                    placeholder="Opposite Phoenix Mall"
                    className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                  />
                </Field>
              </div>
              <div className="space-y-2">
                <div className="text-[11px] tracking-wider uppercase font-bold"
                     style={{ color: 'hsl(var(--ax-text-muted))' }}>
                  Drop a pin
                </div>
                <ArenaxLeafletPicker
                  value={form.geo}
                  onChange={geo => update('geo', geo)}
                />
                {form.geo?.lat ? (
                  <p className="text-[11px]" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                    Pin: {form.geo.lat.toFixed(5)}, {form.geo.lng.toFixed(5)}
                  </p>
                ) : (
                  <p className="text-[11px] italic" style={{ color: 'hsl(var(--ax-text-faint))' }}>
                    Click anywhere on the map to set your venue's location.
                  </p>
                )}
              </div>
            </div>
          </Section>

          {/* ---- Infrastructure ---- */}
          <Section title="Infrastructure" subtitle="The basics players check before booking.">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Field label="Turf / Court count">
                <input
                  type="number" min={1}
                  data-testid="host-venue-turf-count"
                  value={form.infrastructure.turf_count ?? ''}
                  onChange={e => updateNested('infrastructure', 'turf_count', e.target.value ? Number(e.target.value) : null)}
                  className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                />
              </Field>
              <Field label="Ground size (sq ft)">
                <input
                  type="number" min={0}
                  data-testid="host-venue-ground-size"
                  value={form.infrastructure.ground_size_sqft ?? ''}
                  onChange={e => updateNested('infrastructure', 'ground_size_sqft', e.target.value ? Number(e.target.value) : null)}
                  placeholder="20000"
                  className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                />
              </Field>
              <Field label="Seating capacity">
                <input
                  type="number" min={0}
                  data-testid="host-venue-seating"
                  value={form.infrastructure.seating_capacity ?? ''}
                  onChange={e => updateNested('infrastructure', 'seating_capacity', e.target.value ? Number(e.target.value) : null)}
                  placeholder="50"
                  className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
              {[
                { k: 'indoor', label: 'Indoor' },
                { k: 'outdoor', label: 'Outdoor' },
                { k: 'floodlights', label: 'Floodlights' },
                { k: 'washrooms', label: 'Washrooms' },
                { k: 'parking', label: 'Parking' },
              ].map(opt => (
                <label
                  key={opt.k}
                  className="flex items-center gap-2 cursor-pointer px-3 py-2.5 rounded-lg border"
                  style={{ borderColor: 'var(--ax-border-quiet)', background: 'var(--ax-card-bg-soft)' }}
                  data-testid={`host-venue-infra-${opt.k}`}
                >
                  <input
                    type="checkbox"
                    checked={!!form.infrastructure[opt.k]}
                    onChange={e => updateNested('infrastructure', opt.k, e.target.checked)}
                    className="h-3.5 w-3.5"
                  />
                  <span className="text-xs font-semibold" style={{ color: 'hsl(var(--ax-text))' }}>
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>

            <div className="mt-5">
              <Field label="Surface">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="host-venue-surfaces">
                  {SURFACES.map(s => {
                    const active = form.surface_type === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => update('surface_type', s.id)}
                        data-testid={`host-venue-surface-${s.id}`}
                        className="text-xs font-semibold px-3 py-2.5 rounded-lg border transition-all"
                        style={{
                          background: active ? 'var(--ax-primary-gradient)' : 'var(--ax-card-bg-soft)',
                          color: active ? 'hsl(var(--ax-button-text))' : 'hsl(var(--ax-text-muted))',
                          borderColor: active ? 'transparent' : 'var(--ax-border-quiet)',
                        }}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Lighting">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="host-venue-lighting">
                  {LIGHTING.map(l => {
                    const active = form.lighting === l.id;
                    const Icon = l.icon;
                    return (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => update('lighting', l.id)}
                        data-testid={`host-venue-lighting-${l.id}`}
                        className="text-xs font-semibold px-3 py-2.5 rounded-lg border transition-all flex items-center gap-1.5"
                        style={{
                          background: active ? 'var(--ax-primary-gradient)' : 'var(--ax-card-bg-soft)',
                          color: active ? 'hsl(var(--ax-button-text))' : 'hsl(var(--ax-text-muted))',
                          borderColor: active ? 'transparent' : 'var(--ax-border-quiet)',
                        }}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {l.label}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Amenities">
                <div className="flex flex-wrap gap-2" data-testid="host-venue-amenities">
                  {AMENITIES.map(a => {
                    const active = form.amenities.includes(a);
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => toggleArr('amenities', a)}
                        data-testid={`host-venue-amenity-${a.toLowerCase().replace(/\s/g, '-')}`}
                        className={`arenax-chip ${active ? 'is-active' : ''} text-xs px-3 py-1.5 rounded-full transition-all`}
                      >
                        {a}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>
          </Section>

          {/* ---- Booking Capability ---- */}
          <Section title="Booking" subtitle="The supply your players can claim.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Base price / hour (₹)">
                <input
                  type="number" min={0}
                  data-testid="host-venue-base-price"
                  value={form.booking.base_price_per_hour ?? ''}
                  onChange={e => updateNested('booking', 'base_price_per_hour', e.target.value ? Number(e.target.value) : null)}
                  placeholder="1200"
                  className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                />
              </Field>
              <Field label="Peak price / hour (₹)" helper="Floodlit evenings, weekends.">
                <input
                  type="number" min={0}
                  data-testid="host-venue-peak-price"
                  value={form.booking.peak_price_per_hour ?? ''}
                  onChange={e => updateNested('booking', 'peak_price_per_hour', e.target.value ? Number(e.target.value) : null)}
                  placeholder="1800"
                  className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <label className="flex items-start gap-2 px-3 py-2.5 rounded-lg border cursor-pointer"
                     style={{ borderColor: 'var(--ax-border-quiet)', background: 'var(--ax-card-bg-soft)' }}
                     data-testid="host-venue-open-slots">
                <input type="checkbox"
                       checked={form.booking.open_slots}
                       onChange={e => updateNested('booking', 'open_slots', e.target.checked)}
                       className="mt-0.5 h-3.5 w-3.5" />
                <span className="text-xs" style={{ color: 'hsl(var(--ax-text))' }}>
                  <span className="font-bold block">Open slots immediately</span>
                  <span style={{ color: 'hsl(var(--ax-text-muted))' }}>
                    Players can book the moment your venue goes live.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 px-3 py-2.5 rounded-lg border cursor-pointer"
                     style={{ borderColor: 'var(--ax-border-quiet)', background: 'var(--ax-card-bg-soft)' }}
                     data-testid="host-venue-subscription-available">
                <input type="checkbox"
                       checked={form.booking.subscription_available}
                       onChange={e => updateNested('booking', 'subscription_available', e.target.checked)}
                       className="mt-0.5 h-3.5 w-3.5" />
                <span className="text-xs" style={{ color: 'hsl(var(--ax-text))' }}>
                  <span className="font-bold block">Subscription / packages</span>
                  <span style={{ color: 'hsl(var(--ax-text-muted))' }}>
                    Weekly slots, monthly memberships.
                  </span>
                </span>
              </label>
            </div>
          </Section>

          {/* ---- Media ---- */}
          <Section title="Media" subtitle="Supply-side trust is visual. The first image controls conversion.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ArenaxImageUploader
                label="Cover image"
                kind="cover"
                value={form.cover_image}
                onChange={img => update('cover_image', img)}
                testId="host-venue-cover-upload"
                helper="Hero image shown on venue cards."
              />
              <ArenaxImageUploader
                label="Logo (optional)"
                kind="logo"
                value={form.logo_image}
                onChange={img => update('logo_image', img)}
                testId="host-venue-logo-upload"
                helper="Square brand mark for venue cards."
              />
            </div>
            <div className="mt-5">
              <ArenaxGalleryUploader
                label="Turf gallery"
                value={form.gallery_images}
                onChange={g => update('gallery_images', g)}
                testId="host-venue-gallery"
                helper="Add 4-8 sharp shots — turf, floodlights, courts, amenities."
              />
            </div>
          </Section>

          {/* ---- Publish ---- */}
          <Section title="Publish">
            <Field label="Contact phone">
              <input
                data-testid="host-venue-phone"
                value={form.phone}
                onChange={e => update('phone', e.target.value)}
                placeholder="+91 98765 43210"
                className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
              />
            </Field>
            <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border transition-all mt-4"
                   style={{ borderColor: form.publish_immediately ? 'rgba(16,185,129,0.40)' : 'var(--ax-border-quiet)',
                            background: form.publish_immediately ? 'rgba(16,185,129,0.06)' : 'transparent' }}
                   data-testid="host-venue-publish-toggle">
              <input
                type="checkbox"
                checked={form.publish_immediately}
                onChange={e => update('publish_immediately', e.target.checked)}
                className="mt-1 h-4 w-4 cursor-pointer"
              />
              <div>
                <div className="text-sm font-bold" style={{ color: 'hsl(var(--ax-text))' }}>
                  List in the city pulse immediately
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                  Your venue appears in searches and player discovery as soon as you submit.
                </div>
              </div>
            </label>
          </Section>

          {/* ---- Footer ---- */}
          <div className="flex items-center justify-between pt-4 border-t"
               style={{ borderColor: 'var(--ax-border-quiet)' }}>
            <button
              type="button"
              onClick={() => navigate('/arenax/host')}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg"
              style={{ color: 'hsl(var(--ax-text-muted))' }}
              data-testid="host-venue-back"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to hub
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !canSubmit}
              className="inline-flex items-center gap-1.5 text-xs font-bold py-2.5 px-5 rounded-lg transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
              style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))',
                       boxShadow: 'var(--ax-shadow-glow)' }}
              data-testid="host-venue-submit"
            >
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              List venue <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </section>
    </ArenaxLayout>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-bold tracking-tight"
            style={{ color: 'hsl(var(--ax-text))' }}>{title}</h2>
        {subtitle && (
          <p className="text-xs mt-0.5"
             style={{ color: 'hsl(var(--ax-text-muted))' }}>{subtitle}</p>
        )}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, helper, children }) {
  return (
    <div>
      <label className="text-[11px] tracking-wider uppercase font-bold block mb-1.5"
             style={{ color: 'hsl(var(--ax-text-muted))' }}>
        {label}
      </label>
      {children}
      {helper && (
        <p className="text-[11px] mt-1.5" style={{ color: 'hsl(var(--ax-text-faint))' }}>{helper}</p>
      )}
    </div>
  );
}
